import type {
  AnalysisSnapshot,
  PlaneBasis,
  RegionBounds2D,
  RegionGrowOptions,
  RegionGrowResult,
  TexelDensityResult,
} from './types';
import { weldVertices } from './mesh.weld';
import { buildAdjacency } from './mesh.adjacency';
import { computeFaceAreas, computeFaceNormals, smoothFaceNormals } from './normals';
import { growPlanarRegion } from './region.grow';
import { closeRegionHoles } from './region.filter';
import { computePlaneBasis, computeRegionBounds2D, computeVertexWaveDepths } from './region.basis';
import { computeTexelDensity } from './capture.resolution';

/**
 * Topology + smoothed normals that don't depend on the tap point. Computed ONCE
 * per loaded model (ideally in the worker, while the user orbits) so each tap is
 * cheap. Pure: safe to run on a worker thread.
 */
export interface Precomputed {
  snapshot: AnalysisSnapshot;
  weldedPositions: Float32Array;
  weldedIndices: Uint32Array;
  remap: Uint32Array;
  weldedCount: number;
  adjacent: Int32Array;
  smoothedNormals: Float32Array;
  areas: Float32Array;
}

/** Everything a tap produces: the region, its frame, bounds, density and FX depths. */
export interface TapResult {
  region: RegionGrowResult;
  basis: PlaneBasis;
  bounds: RegionBounds2D;
  density: TexelDensityResult;
  vertexDepths: Float32Array[]; // per source mesh, per original vertex (ejDepth attribute)
}

export function precomputeAnalysis(
  snapshot: AnalysisSnapshot,
  opts: { smoothIterations?: number; weldToleranceRel?: number } = {},
): Precomputed {
  const smoothIterations = opts.smoothIterations ?? 1;
  const weldTol = (opts.weldToleranceRel ?? 1e-4) * (snapshot.bboxDiag || 1);
  const weld = weldVertices(snapshot.positions, snapshot.indices, weldTol);
  const adj = buildAdjacency(weld.weldedIndices, weld.weldedCount);
  const normals = computeFaceNormals(weld.weldedPositions, weld.weldedIndices);
  const areas = computeFaceAreas(weld.weldedPositions, weld.weldedIndices);
  const smoothedNormals = smoothFaceNormals(normals, areas, adj, smoothIterations);
  return {
    snapshot,
    weldedPositions: weld.weldedPositions,
    weldedIndices: weld.weldedIndices,
    remap: weld.remap,
    weldedCount: weld.weldedCount,
    adjacent: adj.adjacent,
    smoothedNormals,
    areas,
  };
}

export function analyzeTap(pre: Precomputed, seedTriangle: number, opts?: RegionGrowOptions): TapResult {
  const adj = { adjacent: pre.adjacent };
  const grow = growPlanarRegion(
    pre.weldedPositions,
    pre.weldedIndices,
    adj,
    pre.smoothedNormals,
    pre.areas,
    seedTriangle,
    pre.snapshot.bboxDiag,
    opts,
  );
  const region = closeRegionHoles(grow, adj, pre.snapshot.triangleCount);
  const basis = computePlaneBasis(pre.weldedPositions, pre.weldedIndices, region.triangles, region.plane);
  const bounds = computeRegionBounds2D(pre.weldedPositions, pre.weldedIndices, region.triangles, basis);
  // Texel density uses the ORIGINAL (un-welded) UVs + positions + per-mesh textures.
  const density = computeTexelDensity(
    pre.snapshot.positions,
    pre.snapshot.uvs,
    pre.snapshot.indices,
    region.triangles,
    pre.snapshot.triMeshIndex,
    pre.snapshot.meshes,
  );
  const vertexDepths = computeVertexWaveDepths(
    region,
    pre.weldedIndices,
    pre.remap,
    pre.snapshot.meshVertexOffset,
    pre.snapshot.vertexCount,
    pre.weldedCount,
  );
  return { region, basis, bounds, density, vertexDepths };
}
